import React from 'react';
import { Link } from 'react-router-dom';
import ButtonComponent from '../../Components/common/buttons/ButtonComponent/ButtonComponent';
import { buttonVariants } from '../../Components/common/buttons/types';
import PercentageCircle from '../../Components/common/PercentageCircle/PercentageCircle';
import timesheetIcon from '../../Assets/images/dashboard/timesheetIcon.svg';
import expenseIcon from '../../Assets/images/dashboard/expenseIcon.svg';
import { NotificationCard } from '../../Components/common/Cards/NotificationCard/NotificationCard';
import TimeandExpenseCard from '../../Components/common/Cards/TimeandExpenseCard/TimeandExpenseCard';
import onboardingIcon from '../../Assets/images/dashboard/onboardingIcon.svg';
import { generalEnums } from '../../utils/enums';
import AddGradientIcon from '../../Components/common/Icons/AddGradientIcon';
import { useWindowSize } from '../../utils/hooks';
import { CustomTooltip } from '../../Components/common/Tooltip/Tooltip';
import colors from '../../scss/colors';
import { responsiveSizeTimesheet } from '../../utils/constants';
import MainButton from '../../Components/common/buttons/MainButton';

const CreateButton = ({ link, title }) => {
  const { width } = useWindowSize();

  return (
    <CustomTooltip
      title={title}
      placement="bottom-start"
      theme={{
        backgroundColor: colors.NAVY_BLUE,
        color: colors.WHITE_TXT,
        padding: '6px 8px',
        fontSize: 13,
        borderRadius: '8px'
      }}
    >
      <Link to={link} onClick={(e) => e.stopPropagation()}>
        <AddGradientIcon size={width > 768 ? '42px' : '24px'} />
      </Link>
    </CustomTooltip>
  );
};

const contractorConfig = ({
  t,
  navigate,
  setSearchField,
  searchField,
  accessTo,
  timesheetValidationStatus,
  expenseValidationStatus,
  timesheetPlacementStatus,
  expensePlacementStatus,
  timesheetCountsContractor,
  expensesCountsContractor,
  validationPending,
  handleTimesheetStatusClick,
  handleExpenseStatusClick
}) => {
  const { width } = useWindowSize();

  const handleProfileContinue = () => {
    navigate('/my-profile');
  };

  const handleSearchJobs = () => {
    navigate({
      pathname: '/dashboard/jobs',
      search: `?searchTxt=${searchField}`
    });
  };

  return [
    {
      id: 'onboarding',
      order: 1,
      icon: onboardingIcon,
      title: t('dashboard.cards.onboarding'),
      isDualLayout: true,
      cardSize: 'card-sm',
      isCustom: false,
      leftContent: null,
      clickAction: '/onboardingInteraction',
      child: (styles) => (
        <div className={styles.profileWrapper}>
          <h5>{t('dashboard.cards.getreadyfirstday')}</h5>
          <button className={styles.continueButton}>{t('common.general.continue')}</button>
        </div>
      )
    },
    {
      id: 'jobs',
      order: accessTo.onboarding ? 4 : 3,
      icon: null,
      title: 'Jobs',
      isDualLayout: false,
      cardSize: 'card-lg',
      isCustom: false,
      leftContent: null,
      child: (styles) => (
        <>
          <h5>{t('dashboard.cards.searchrole')}</h5>
          <input
            onChange={(e) => setSearchField(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearchJobs();
            }}
            type="text"
            placeholder="Keyword eg: Engineer"
            className={styles.searchInput}
          />
          <div className={width > responsiveSizeTimesheet ? styles.jobWrapperLarge : styles.jobWrapperSmall}>
            <Link to={`/dashboard/jobs?searchTxt=${searchField}`} onClick={(e) => e.stopPropagation()}>
              <MainButton
                rounded
                forceWidth="max-content"
                size="small"
                variant={buttonVariants.SECONDARY}
                sx={{
                  marginTop: '10px',
                }}
              >
                {width > responsiveSizeTimesheet ? t('dashboard.cards.searchjobs') : t('common.general.showresults')}
              </MainButton>
            </Link>
          </div>
        </>
      )
    },
    {
      id: 'profile',
      order: accessTo.onboarding ? 2 : 4,
      icon: null,
      title: t('dashboard.cards.myprofile'),
      isDualLayout: true,
      cardSize: 'card-sm',
      isCustom: true,
      clickAction: '/my-profile',
      leftContent: <PercentageCircle percentage={100} />,
      child: (styles) => (
        <div className={styles.profileWrapper}>
          {width > responsiveSizeTimesheet ? (
            <h5>{t('dashboard.cards.profileuptodate')}</h5>
          ) : (
            <p>{t('dashboard.cards.profileuptodate')}</p>
          )}
          {width > responsiveSizeTimesheet && (
            <button onClick={handleProfileContinue} className={styles.continueButton}>
              {t('common.general.continue')}
            </button>
          )}
        </div>
      )
    },
    {
      id: 'notification',
      order: 5,
      icon: null,
      title: t('dashboard.cards.notifications'),
      isDualLayout: false,
      cardSize: 'card-md',
      isCustom: false,
      leftContent: null,
      child: () => <NotificationCard />
    },
    {
      id: 'timesheet',
      order: 6,
      icon: timesheetIcon,
      title: t('dashboard.cards.timesheets'),
      isDualLayout: true,
      cardSize: 'card-sm',
      clickAction: '/dashboard/timesheets',
      isCustom: false,
      leftContent: null,
      extraComponent: timesheetValidationStatus && timesheetPlacementStatus && width > responsiveSizeTimesheet && (
        <CreateButton link="/dashboard/create-timesheet" title={t('common.navigationheaders.createtimesheet')} />
      ),
      child: (styles) => (
        <TimeandExpenseCard
          type={generalEnums.TIMESHEET}
          styles={styles}
          status={timesheetValidationStatus}
          placementStatus={timesheetPlacementStatus}
          pending={validationPending}
          list={timesheetCountsContractor}
          handleButtonClick={handleTimesheetStatusClick}
        />
      )
    },
    {
      id: 'expense',
      order: 7,
      icon: expenseIcon,
      title: t('dashboard.cards.expenses'),
      isDualLayout: true,
      cardSize: 'card-sm',
      isCustom: false,
      leftContent: null,
      clickAction: '/dashboard/expenses',
      extraComponent: expenseValidationStatus && expensePlacementStatus && width > responsiveSizeTimesheet && (
        <CreateButton link="/dashboard/create-claim" title={t('common.navigationheaders.createclaim')} />
      ),
      child: (styles) => (
        <TimeandExpenseCard
          type={generalEnums.EXPENSE}
          styles={styles}
          status={expenseValidationStatus}
          placementStatus={expensePlacementStatus}
          pending={validationPending}
          list={expensesCountsContractor}
          handleButtonClick={handleExpenseStatusClick}
        />
      )
    }
  ];
};

const approverConfig = ({
  t,
  timesheetValidationStatus,
  expenseValidationStatus,
  validationPending,
  pendingTandECounts
}) => {
  return [
    {
      id: 'timesheet',
      order: 1,
      icon: timesheetIcon,
      title: t('dashboard.cards.timesheets'),
      isDualLayout: true,
      isCustom: false,
      leftContent: null,
      clickAction: '/dashboard/timesheets',
      child: (styles) => (
        <TimeandExpenseCard
          type={generalEnums.TIMESHEET}
          styles={styles}
          status={timesheetValidationStatus}
          pending={validationPending}
          list={pendingTandECounts?.PendingTimesheetCount}
        />
      )
    },
    {
      id: 'expense',
      order: 2,
      icon: expenseIcon,
      title: t('dashboard.cards.expenses'),
      isDualLayout: true,
      isCustom: false,
      leftContent: null,
      clickAction: '/dashboard/expenses',
      child: (styles) => (
        <TimeandExpenseCard
          type={generalEnums.EXPENSE}
          styles={styles}
          status={expenseValidationStatus}
          pending={validationPending}
          list={pendingTandECounts?.PendingClaimsCount}
        />
      )
    }
  ];
};

export { contractorConfig, approverConfig };
